import { useState, useEffect, useMemo } from 'react';
import { Listbox, Transition } from '@headlessui/react';

export default function Select({
  valueDefault = '',
  options = [],
  onSelected,
  label,
  className = '',
  placeholder,
}) {
  const [valueSelected, setValueSelected] = useState(() => options.find((o) => o.value === valueDefault) || {});

  const selectedContent = useMemo(() => {
    if (!Object.entries(valueSelected || {}).length || !valueSelected.value) {
      return placeholder;
    }
    return valueSelected.contentSelected || valueSelected.content || valueSelected.title;
  }, [valueSelected, placeholder]);

  useEffect(() => {
    onSelected?.(valueSelected);
  }, [valueSelected, onSelected]);

  return (
    <Listbox as="div" value={valueSelected.value} onChange={setValueSelected}>
      {({ open }) => (
        <>
          {label && (
            <Listbox.Label className="block leading-6 text-dark">
              {label}
            </Listbox.Label>
          )}
          <div className={`relative ${className}`}>
            <span className="inline-block w-full py-1 rounded-md lg:py-0">
              <Listbox.Button className="relative w-full py-1.5 pl-4 pr-10 text-left transition duration-150 ease-in-out border rounded-md cursor-pointer bg-purple-5 min-w-24 border-purple-4/30 focus:outline-none focus:border-purple-4/20 sm:leading-6">
                <span className="block text-base truncate">
                  {selectedContent}
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-1 pointer-events-none">
                  <svg className="w-5 h-5 text-dark" viewBox="0 0 20 20" fill="none" stroke="currentColor">
                    <path
                      d="M7 7l3-3 3 3m0 6l-3 3-3-3"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </Listbox.Button>
            </span>
            <Transition
              as="div"
              show={open}
              enter="transition ease-in duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              className="absolute z-20 w-full mb-1 rounded-md bg-purple-5 shadow-button bottom-full"
            >
              <Listbox.Options
                static
                className="overflow-auto text-base leading-6 border rounded-md shadow-xs max-h-60 focus:outline-none sm:leading-5 border-purple/50"
              >
                {options.map((option) => (
                  <Listbox.Option key={option.value} value={option}>
                    {({ selected, active }) => (
                      <div
                        className={`${
                          active ? 'text-white bg-purple inline-block w-full rounded-md' : 'text-white'
                        } cursor-pointer relative`}
                      >
                        {option.content || (
                          <span className={`${selected ? 'font-medium' : 'font-normal'} block truncate`}>
                            {option.title}
                          </span>
                        )}
                        {selected && (
                          <span
                            className={`${
                              active ? 'text-white' : 'text-purple'
                            } absolute inset-y-0 left-0 flex items-center pl-1.5`}
                          >
                            <svg
                              className="w-5 h-5"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                        )}
                      </div>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );
}
